import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { KpiData } from './types';
import type { KpiDrilldownSpec } from './kpiDashboardApi';
import { SectionLabel } from './KpiDashboardContent';
import KpiProblemsMonthlyLineChart from './charts/KpiProblemsMonthlyLineChart';
import KpiProblemMatrixTable from './charts/KpiProblemMatrixTable';
import { KPI_DASHBOARD_TABLE_TH } from './kpiDashboardTableStyles';
import { formatKpiRatePercentFromNumberString } from './kpiDashboardNumberFormat';
export interface KpiProblemDashboardTabProps {
  chartsData: KpiData | undefined;
  chartsLoading: boolean;
  chartsFetching: boolean;
  chartsError: Error | null;
  chartsOnRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
}

function ProblemKpiHeaderStrip({
  problemCount,
  knowledgePct,
  onMetric,
}: {
  problemCount: string;
  knowledgePct: string;
  onMetric: (key: string) => void;
}) {
  const items = [
    { label: 'Problem Count KPI', value: problemCount, drillKey: 'problems_volume' as const },
    {
      label: 'Problems with Knowledge (documented solution)',
      value: formatKpiRatePercentFromNumberString(knowledgePct),
      drillKey: null,
    },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500" aria-hidden />
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        {items.map((item, i) => (
          <div key={item.label}>
            {item.drillKey ? (
              <button
                type="button"
                onClick={() => onMetric(item.drillKey!)}
                className="flex w-full flex-col items-start px-5 py-5 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4472C4] sm:px-6"
              >
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
                <span
                  className={`mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900 ${
                    i === 0 ? 'underline decoration-slate-900 decoration-1 underline-offset-4' : ''
                  }`}
                >
                  {item.value}
                </span>
              </button>
            ) : (
              <div className="flex flex-col items-start px-5 py-5 sm:px-6">
                <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
                <span className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900">{item.value}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** @see kpiDashboardTableStyles */
const KPI_TABLE_TH = KPI_DASHBOARD_TABLE_TH;

function formatOpened(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

const KpiProblemDashboardTab: React.FC<KpiProblemDashboardTabProps> = ({
  chartsData,
  chartsLoading,
  chartsFetching,
  chartsError,
  chartsOnRetry,
  onDrill,
}) => {
  const openMetric = useCallback(
    (key: string) => {
      onDrill({ kind: 'metric', key });
    },
    [onDrill]
  );

  const stateColumns = useMemo(() => {
    const h = chartsData?.problemsStateCustomerHeatmap;
    if (!h?.stateLabels.length) return [];
    return h.stateLabels.map((label, i) => ({ label, key: h.stateKeys[i] ?? String(i) }));
  }, [chartsData?.problemsStateCustomerHeatmap]);

  const weekColumns = useMemo(() => {
    const h = chartsData?.problemsCustomerWeekHeatmap;
    if (!h?.weekLabels.length) return [];
    return h.weekLabels.map((label, i) => ({ label, key: h.weekStarts[i] ?? String(i) }));
  }, [chartsData?.problemsCustomerWeekHeatmap]);

  const loading = chartsLoading && !chartsData;
  if (loading) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading problems dashboard">
        <div className="h-24 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="h-64 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-4" />
          <div className="h-64 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-4" />
          <div className="h-64 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-4" />
        </div>
      </div>
    );
  }

  const blockingError = chartsError && !chartsData;
  if (blockingError) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load problems dashboard</p>
        <p className="mt-1 text-sm text-red-700/90">{chartsError?.message}</p>
        {chartsOnRetry && (
          <button
            type="button"
            onClick={() => chartsOnRetry()}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!chartsData) return null;

  const hState = chartsData.problemsStateCustomerHeatmap;
  const hWeek = chartsData.problemsCustomerWeekHeatmap;

  return (
    <div className="relative">
      {chartsFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {chartsError && chartsData && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">Showing cached data. Latest refresh failed: {chartsError.message}</span>
          <button
            type="button"
            onClick={() => chartsOnRetry?.()}
            className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
          >
            Retry
          </button>
        </div>
      )}

      <section className="mb-6">
        <SectionLabel>Problem KPIs</SectionLabel>
        <ProblemKpiHeaderStrip
          problemCount={chartsData.problemsVolume.toLocaleString()}
          knowledgePct={chartsData.problemsWithKnowledgePct}
          onMetric={openMetric}
        />
      </section>

      <section className="mb-6">
        <SectionLabel>Trends &amp; distribution</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <KpiProblemsMonthlyLineChart
              monthlyTrendProblem={chartsData.monthlyTrendProblem}
              monthLabels={chartsData.monthlyTrendMonthLabels}
              onDrill={onDrill}
            />
          </div>
          <div className="min-h-0 lg:col-span-8">
            {stateColumns.length > 0 ? (
              <KpiProblemMatrixTable
                title="Problems by State & Customer"
                columns={stateColumns}
                rows={hState.rows}
                maxCount={hState.maxCount}
                drillMode="state"
                onCellDrill={onDrill}
                emptyMessage="No problems with customer assignment in this range."
              />
            ) : (
              <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200/90 bg-white p-6 text-sm text-slate-500 shadow-sm">
                No state × customer data for the selected filters.
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Problems by customer</SectionLabel>
        <div className="w-full min-h-0">
          {weekColumns.length > 0 ? (
            <KpiProblemMatrixTable
              title="Problems by Customer"
              columns={weekColumns}
              rows={hWeek.rows}
              maxCount={hWeek.maxCount}
              drillMode="week"
              onCellDrill={onDrill}
              emptyMessage="No problems by customer/week in this range."
            />
          ) : (
            <div className="flex min-h-[200px] items-center justify-center rounded-xl border border-slate-200/90 bg-white p-6 text-sm text-slate-500 shadow-sm">
              No weekly customer data for the selected filters.
            </div>
          )}
        </div>
      </section>

      <section className="mb-2">
        <SectionLabel>Problem details</SectionLabel>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="max-h-[min(28rem,70vh)] overflow-auto">
            <table className="w-full min-w-[56rem] border-collapse text-xs sm:text-sm">
              <thead>
                <tr>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Number
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    State
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Opened
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Assignment Group
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Company
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Knowledge
                  </th>
                  <th className={`${KPI_TABLE_TH} whitespace-nowrap`} scope="col">
                    Priority
                  </th>
                  <th className={`${KPI_TABLE_TH} min-w-[12rem]`} scope="col">
                    Problem Statement
                  </th>
                </tr>
              </thead>
              <tbody>
                {chartsData.problemsDetailPreview.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500">
                      No problems in this range.
                    </td>
                  </tr>
                ) : (
                  chartsData.problemsDetailPreview.map((row, i) => (
                    <tr
                      key={row.id}
                      className={i % 2 === 0 ? 'bg-white hover:bg-slate-50/80' : 'bg-slate-50/80 hover:bg-slate-50/80'}
                    >
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 font-medium text-[#2F5597]">
                        <Link to={`/problems/${row.id}`} className="hover:underline">
                          {row.number}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-slate-800">{row.stateLabel}</td>
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-slate-800">{formatOpened(row.openedAt)}</td>
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-slate-800">{row.assignmentGroup ?? '—'}</td>
                      <td className="max-w-[10rem] truncate border border-slate-200 px-2 py-1.5 text-slate-800" title={row.company ?? ''}>
                        {row.company ?? '—'}
                      </td>
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-slate-800">{row.knowledge}</td>
                      <td className="whitespace-nowrap border border-slate-200 px-2 py-1.5 text-slate-800">{row.priorityLabel}</td>
                      <td className="max-w-[18rem] border border-slate-200 px-2 py-1.5 text-slate-800" title={row.problemStatement}>
                        <span className="line-clamp-2">{row.problemStatement || '—'}</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

    </div>
  );
};

export default KpiProblemDashboardTab;
